/* @file Provides hooks for interacting with the UserContext.*/
import { useCallback, useContext, useEffect, useState } from 'react';
import { UserContext } from '../context';
import { getApiConfig, getApiUri } from '../configUtils';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';

/** @function
 * Adds user details to a form using the Freshworks Widget.
 * @param {Object} userDetails - user details containing email, given_name, and family_name.
 */
export const addUserDetailsToForm = (userDetails) => {
    const email = userDetails.email;
    const fullName = `${userDetails.firstName} ${userDetails.lastName}`;

    if (window.FreshworksWidget && email && fullName) {
        window.FreshworksWidget('identify', 'ticketForm', {
            name: fullName,
            email: email
        });
    }
};

/** @function
 * Hook for interacting with the userContext
 * @returns {{setUser: function(any), user: Object}}
 */
export function useUser() {
    const { user, setUser } = useContext(UserContext);

    if (user === undefined) {
        throw new Error('User Context is not defined.');
    }

    return { user, setUser };
}

/** @function
 * Hook that hydrates the user on authentication change.
 *
 * This hook should be used sparingly...in a high level component as it automatically performs requests.
 *
 * @returns {{isHydrated: boolean}}
 */
export function useUserHydration() {
    const { setUser } = useUser();
    const {
        user: auth0Details,
        isAuthenticated,
        getAccessTokenSilently
    } = useAuth0();
    const [isHydrated, setIsHydrated] = useState(false);

    useEffect(() => {
        if (auth0Details && isAuthenticated) {
            const hydrate = async () => {
                const config = getApiConfig();
                const apiUri = getApiUri(config) + 'user';
                const token = await getAccessTokenSilently();
                const requestParams = {
                    headers: {
                        Authorization: `Bearer ${token}`
                    },
                    params: { userId: auth0Details.sub }
                };

                const response = await axios.get(apiUri, requestParams);

                let internalDetails = response.data.items[0] || null;

                // Check for subscription type mismatch
                if (
                    internalDetails &&
                    auth0Details.subscription_type !==
                        internalDetails.subscriptionType
                ) {
                    // Construct the updated details
                    const updatedDetails = {
                        ...internalDetails,
                        subscriptionType: auth0Details.subscription_type
                    };

                    try {
                        // Send the PUT request to update the backend
                        await axios.put(apiUri, updatedDetails, {
                            headers: {
                                Authorization: `Bearer ${token}`
                            }
                        });
                    } catch (error) {
                        console.error(
                            'Failed to update subscription type:',
                            error
                        );
                    }
                }

                setIsHydrated(true);
                setUser({
                    auth0Details: auth0Details,
                    internalDetails: internalDetails
                });
                // add user details to FreshworksWidget
                addUserDetailsToForm({
                    email: auth0Details.email,
                    firstName: auth0Details.given_name,
                    lastName: auth0Details.family_name
                });
            };
            hydrate();
        }
    }, [auth0Details, isAuthenticated]);

    return { isHydrated };
}

/** @function
 * Hook for interacting with the UserContext
 * acts as a factory for associated functions.
 * @returns {{getPrettyName: ((function(): string)), getInitials: (function(): string), getCompanyName: ((function(): string)), getRole: ((function(): string)), getEmail: ((function(): string)), isRegistered: ((function(): (undefined))|boolean), hydrate: ((function(): Promise<void>)|*), isAuthenticated: boolean, getFirstName: ((function(): string)), getLastName: ((function(): string))}}
 */
export function useUserFields() {
    const { user } = useUser();

    /** @function
     * Higher order function for creating exposed getters.
     * @param userAttribute
     * @param internalUserAttribute
     * @returns {(function(): (*|string))|*}
     */
    function createPrecedenceFunc(userAttribute, internalUserAttribute) {
        return () => {
            if (user.internalDetails?.[internalUserAttribute] !== undefined) {
                return user.internalDetails[internalUserAttribute];
            }
            if (user.auth0Details?.[userAttribute]) {
                return user.auth0Details[userAttribute];
            }

            return '';
        };
    }

    /** @function
     * Gets the first Name of the user, giving precedence to internal user details.
     * @return {string} - first name of user.
     * */
    const getFirstName = useCallback(
        createPrecedenceFunc('given_name', 'firstName'),
        [user]
    );

    /** @function
     * Gets the last name of the user, giving precedence to internal user details.
     * @return {string} - last name of user
     */
    const getLastName = useCallback(
        createPrecedenceFunc('family_name', 'lastName'),
        [user]
    );

    /** @function
     * Gets the initials user, giving precedence to internal user details.
     * @return {string} - last name of user
     */
    const getInitials = useCallback(() => {
        let initials = '';

        const firstName = getFirstName();
        const lastName = getLastName();

        if (firstName) {
            initials += firstName.substring(0, 1);
        }

        if (lastName) {
            initials += lastName.substring(0, 1);
        }
        return initials;
    }, [user]);

    /** @function
     * Gets the email, giving precedence to internal user details.
     * @return {string} - last name of user
     */
    const getEmail = useCallback(createPrecedenceFunc('email', 'email'), [
        user
    ]);

    const getPrettyName = useCallback(() => {
        const firstName = getFirstName();
        const lastName = getLastName();
        if (firstName && lastName) {
            return firstName + ' ' + lastName;
        }
        return '';
    }, [user]);
    /** @function
     * Gets the company name of the internal user.
     * @returns {string} - company name of user.
     */
    const getCompanyName = useCallback(() => {
        if (user.internalDetails?.company) {
            return user.internalDetails.company;
        }
        return '';
    }, [user.internalDetails]);

    /** @function
     * Gets the internal user's role.
     * @returns {string} - The user's role.
     */
    const getRole = useCallback(() => {
        if (user.internalDetails?.role) {
            return user.internalDetails.role;
        }
        return '';
    }, [user.internalDetails]);

    return {
        getFirstName,
        getLastName,
        getInitials,
        getEmail,
        getCompanyName,
        getRole,
        getPrettyName
    };
}

/** @function
 * Gets the user status in the form of accessors.
 *
 * isRegistered() defines whether
 * @returns {{isRegistered: ((function(): undefined|boolean))}}
 */
export function useUserStatus() {
    const { user } = useUser();

    const internalUserAttributes = [
        'userId',
        'email',
        'firstName',
        'lastName',
        'company',
        'role'
    ];

    /** @function
     * Checks whether an internal user is complete, that is, the user has fully registered.
     *
     * Fully registered is defined by the user having been hydrated (internalDetails aren't undefined) and those
     * details either not being present in dynamodb (null) or being present and incomplete.
     *
     * @returns {boolean | undefined} undefined if the user is not hydrated.
     */
    const isRegistered = useCallback(() => {
        // No hydration has occurred.
        if (user.internalDetails === undefined) {
            return undefined;
        }
        if (user.internalDetails === null) {
            return false;
        }
        return internalUserAttributes.every(
            (attr) => user.internalDetails[attr]
        );
    }, [user]);

    return { isRegistered };
}
